import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Color from '@styles/Color';

import Label from '@components/dataEntry/Input/Label';
import keyCode from '@typings/setting/keyCode.enum';
import DropdownWrapper from '@components/wrapper/DropdownWrapper';
import { AnimatePresence } from 'framer-motion';
import ResultDropdown from './ResultDropdown';
import { useCollection } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { IUser } from '@typings/setting/userType.interface';
import InputValidMessage from '@components/dataDisplay/InputValidMessage';

const focusInput = css`
  /* border: 1px solid ${Color.GRAY_8}; */
`;

const Container = styled.div<{
  disabled?: boolean;
  block?: boolean;
  isFocus?: boolean;
}>`
  position: relative;
  min-height: 40px;
  height: auto;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-wrap: wrap;
  padding: 4px 8px;
  border-radius: var(--cardBorderRadius);
  outline: none;
  background-color: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);

  ${({ isFocus }) => isFocus && focusInput}

  gap: 8px;

  input {
    /* flex: 1; */
  }
`;

export const Tag = styled.div`
  padding: 6px 8px;
  background-color: var(--buttonBackgroundColor);
  color: var(--buttonTextColor);
  border-radius: var(--cardBorderRadius);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 14px;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }
`;

interface Props {
  userList: IUser[] | undefined | null;
  setUserList: any;
  onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
  maxLength?: number;
  onlySelect?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  block?: boolean;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  dropdownStyle?: React.CSSProperties;
}

function SearchUserInput({
  maxLength,
  onlySelect,
  label,
  userList,
  setUserList,
  disabled,
  onChange,
  placeholder = '멤버 검색',
  block,
  dropdownStyle,
}: Props) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [isFocus, setIsFocus] = useState(false);
  const handleFocus = useCallback(() => setIsFocus(true), []);
  const handleBlur = useCallback(() => setIsFocus(false), []);
  const [keyword, setkeyowrd] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectUserList, setSelectUserList] = useState<IUser[]>([]);
  const [notFoundMessage, setNotFoundMessage] = useState('');

  const { data: allOriginUserList } = useCollection<IUser>(
    CollectionPath.USERS,
    {
      listen: true,
      orderBy: ['createdAt', 'desc'],
    },
  );
  // 불러온 userList를 한번 가공함. 기본 result
  const [allUserList, setAllUserList] = useState<IUser[]>([]);
  // 서치 결과값이 오는 ㅣist
  const [filteredList, setFilteredList] = useState<IUser[] | null>(
    allUserList || [],
  );

  useEffect(() => {
    // 최초의 userList를 한번 가공하기
    if (allOriginUserList && allOriginUserList.length !== 0) {
      const rebaseList = allOriginUserList.map((item: any) => {
        return {
          id: item.id,
          value: item.displayName,
          thumbnail: item.photoURL || '',
        };
      });

      setAllUserList(rebaseList);
      setFilteredList(rebaseList);
    }
  }, [allOriginUserList]);

  const handleChange = useCallback(
    (e: any) => {
      if (onChange) onChange(e);
      setkeyowrd(e.target.value);
      setNotFoundMessage('');
    },
    [onChange],
  );

  //인풋 value와 기존 input에 있는 유저 중복 여부
  const isDuplicateValue = (targetValue: string): boolean => {
    const result = userList!.filter((item) => item.value === targetValue);
    return result.length !== 0;
  };

  const handleAddUser = (user) => {
    if (onlySelect) {
      setUserList([user]);
      setkeyowrd('');
    } else {
      setUserList([...userList!, user]);
      setkeyowrd(''); //reset
    }
  };

  const handleKeyPress = useCallback(
    (e) => {
      e.stopPropagation();
      if (e.key === keyCode.ENTER && allUserList) {
        const targetValue = e.target.value;
        if (!targetValue) return;

        // 기존의 allUser와 비교
        const matchPrevUser = allUserList.filter(
          (item) => item.value === targetValue,
        );
        if (matchPrevUser.length === 0) {
          // 유저 목록에 없는 경우 → 에러 메시지 표시
          setNotFoundMessage(`"${targetValue}"에 해당하는 유저가 없습니다.`);
        } else {
          setNotFoundMessage('');
          handleSelect();
        }
      }
    },
    [userList, allUserList, filteredList, activeIndex],
  );

  // enter 또는 click 했을 때 적용됨
  const handleSelect = useCallback(
    (index?: number) => {
      let selectedIndex = activeIndex;
      if (!isDuplicateValue(keyword) && filteredList!.length === 1) {
        handleAddUser(filteredList![0]);
      } else {
        if (!index) {
          // console.log('키보드로');
          selectedIndex = activeIndex === 0 ? 0 : activeIndex;
          42;
          const selectedValue = filteredList![selectedIndex].value;
          if (!isDuplicateValue(selectedValue)) {
            handleAddUser(filteredList![selectedIndex]);
          }
        }
        if (index) {
          // console.log('클릭으로');
          selectedIndex = index === 0 ? 0 : index;
          const selectedValue = filteredList![selectedIndex].value;
          if (!isDuplicateValue(selectedValue)) {
            handleAddUser(filteredList![selectedIndex]);
          }
        }
      }
    },
    [activeIndex, filteredList, userList, keyword],
  );

  function HandleRemoveKeyword(index: number) {
    const copyList = [...userList!];
    copyList.splice(index, 1);
    setUserList([...copyList]);
  }

  const handleKeyDown = (e: any) => {
    if (e.key === keyCode.BACKSPACE && !keyword) {
      HandleRemoveKeyword(userList ? userList.length - 1 : 0);
    }
  };

  // 태그 검색
  function handleFilterLists(target) {
    const filterResultList = allUserList!.filter((compareItem) => {
      const { value: originValue } = compareItem;
      const compareTarget = target.toLowerCase().replace(/\s+/g, '');
      const compareOrigin = originValue.toLowerCase().replace(/\s+/g, '');
      return compareOrigin.includes(compareTarget);
    });
    return filterResultList;
  }

  useEffect(() => {
    if (allUserList) {
      setFilteredList(allUserList);
      const result = handleFilterLists(keyword);
      if (keyword.length > 0) {
        handleFocus();
        setFilteredList(result || allUserList);
      }
      if (!keyword) setFilteredList(allUserList);
      if (result.length === 0) handleBlur();
    }
  }, [keyword, allUserList]);

  // activeIndex
  useEffect(() => {
    if (filteredList && keyword) {
      const findIndex = filteredList?.findIndex(
        (list) => list.value === keyword,
      );
      setActiveIndex(findIndex);
    }
  }, [filteredList, keyword]);

  // 드롭다운 바깥 클릭
  function handleClickOutside(e: any, dropdownNode) {
    if (targetRef.current && isFocus && dropdownNode) {
      const isClickOutside =
        !targetRef.current.contains(e.target) &&
        !dropdownNode.contains(e.target);
      if (isClickOutside) handleBlur();
    }
  }

  if (!userList) return null;

  return (
    <>
      {label && <Label>{label}</Label>}
      <Container
        disabled={disabled}
        isFocus={isFocus}
        ref={targetRef}
        style={{ width: block ? '100%' : 'auto' }}
      >
        {userList.map((list, i) => {
          return (
            <Tag key={i} onClick={() => HandleRemoveKeyword(i)}>
              <span>{list.value}</span>
            </Tag>
          );
        })}
        <input
          type="text"
          maxLength={maxLength}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={keyword}
          onChange={handleChange}
          placeholder={placeholder}
          onKeyPress={(e) => handleKeyPress(e)}
          onKeyDown={(e) => handleKeyDown(e)}
        />
        <AnimatePresence>
          {isFocus && allUserList && (
            <DropdownWrapper
              open={isFocus}
              direction="topLeft"
              handleClickOutside={handleClickOutside}
              style={{ width: '100%', ...dropdownStyle }}
              maxHeight={dropdownStyle?.maxHeight ? dropdownStyle.maxHeight as number : 240}
            >
              <ResultDropdown
                isOpen={isFocus}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                filteredList={filteredList}
                handleSelect={handleSelect}
                maxHeight={240}
              />
            </DropdownWrapper>
          )}
        </AnimatePresence>
      </Container>
      {notFoundMessage && (
        <InputValidMessage>{notFoundMessage}</InputValidMessage>
      )}
    </>
  );
}

export default SearchUserInput;
